import { Notice, useEntityForm } from '@lbcamden/lib-react';
import { parseDate } from '@lbcamden/shared-utils';
import { DateTime, Duration } from 'luxon';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { useCurrentUser, useUserStore } from '~/client/hooks/storeHooks';
import { uploadRpeepDocument } from '~/client/lib/upload';
import { PcfraActivityType } from '~/common/types/pcfra';
import { RpeepStatus, PcfraPerson } from '~/common/types/people';

import { CurrentResidentsDialog } from '../CurrentResidents.styled';
import {
  ActivityUpdate,
  RpeepStatusUpdate,
  useHandlePcfraActivity,
} from '../useHandlePcfraActivity';
import {
  getFormConfig,
  getFormSchema,
  isDefaultFormValues,
  isRpeepFormValues,
  isUploadRpeepFormValues,
  LogActivityFormValuesUnion,
} from './config';
import {
  activityTypeRpeepStatusMap,
  getUploadErrorMessage,
  selfReferralActivityTypes,
} from './helpers';

type Props = {
  open?: boolean;
  person?: PcfraPerson;
  onClose(): void;
};

const title = 'Log new activity';

export const LogActivityDialog: FunctionComponent<Props> = ({ open, person, onClose }) => {
  const user = useCurrentUser();

  const userStore = useUserStore();
  const handlePcfraActivity = useHandlePcfraActivity();

  const [selectedActivityType, setSelectedActivityType] = useState<PcfraActivityType>();
  const [errorMessage, setErrorMessage] = useState('');

  const initialValue = useMemo(
    () => ({
      officerName: user?.name,
      date: DateTime.now().toISODate(),
      time: DateTime.now().toFormat('HH:mm'),
      pcfraReviewDate: person?.pcfraReviewDate,
    }),
    [open],
  );

  const hasSelectedSelfReferral =
    selectedActivityType && selfReferralActivityTypes.includes(selectedActivityType);
  const canRevokeRpeep = person?.rpeepStatus === RpeepStatus.Active;

  const onSave = async (formValues: LogActivityFormValuesUnion) => {
    if (!person || !userStore) {
      return;
    }
    const { propertyId, personId } = person;
    const { note, activityType } = formValues;

    let pcfraReviewDate: string | null | undefined; // null unsets an existing value
    let activityUpdate!: RpeepStatusUpdate | ActivityUpdate;

    if (isDefaultFormValues(formValues)) {
      const activityTimestamp = parseDate(formValues.date)
        .plus(Duration.fromISOTime(formValues.time))
        .toISO()!;

      activityUpdate = { activityType, activityTimestamp };
      pcfraReviewDate = formValues.pcfraReviewDate || null;
    }

    if (isRpeepFormValues(formValues)) {
      activityUpdate = { newRpeepStatus: activityTypeRpeepStatusMap[activityType]! };
    }

    if (isUploadRpeepFormValues(formValues)) {
      const { file } = formValues;

      try {
        await uploadRpeepDocument(personId, file);
      } catch (err) {
        setErrorMessage(getUploadErrorMessage(err));
        return;
      }
    }

    handlePcfraActivity({
      propertyId,
      personId,
      activityType,
      note,
      pcfraReviewDate,
      ...activityUpdate,
    });

    onClose();
  };

  const {
    formState: {
      data: { activityType },
      refreshForm,
    },
    form,
  } = useEntityForm({
    schema: getFormSchema(selectedActivityType),
    config: getFormConfig(hasSelectedSelfReferral, canRevokeRpeep),
    initialValue,
    inDialog: true,
    onCancel: onClose,
    onSave,
  });

  useEffect(() => {
    setSelectedActivityType(activityType);
  }, [activityType]);

  useEffect(() => {
    if (open) {
      setErrorMessage('');
      refreshForm(initialValue);
    }
  }, [open]);

  return (
    <CurrentResidentsDialog open={open} title={title} accessibleInfo={{ title }} onClose={onClose}>
      {errorMessage && <Notice type="error" message={errorMessage} />}
      {form}
    </CurrentResidentsDialog>
  );
};
